import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { CallDirection, ICallLog } from '@wephone-app-phone/pages/history/calllog.i';
import { DialogComponentBase } from '@wephone-core-ui';
import { DialogActionButton, DialogService, localNow, parseDateTime } from '@wephone-utils';
import { _tk, _ti } from '@wephone-translation';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CallHistoryItem, CdrService, MissedCallInfo, MissedCallVoicemail } from '@wephone-core/service/cdr_service';
import { CallLogService } from '@wephone-core/service/call_log_service';
import { ConfigManager, EntityManager } from '@wephone-core/wephone-core.module';
import { EnterpriseCrmEntity } from '@wephone-core/model/entity/enterprise_crm';
import { CrmContactService } from '@wephone-core/service/crm-contact.service';
import { VoiceMailRepository } from '@wephone-core/model/repository/voicemail';
import { ISentSmsModalData, SentSmsModalComponent } from '@wephone-sms/component';
import * as _ from 'lodash';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { FeatureName } from '@wephone-core/system';
import { LoggerService } from '@wephone-utils/services/logger.service';

@Component({
  selector: 'calllog-detail',
  templateUrl: 'calllog-detail.html',
  styleUrls: ['./calllog-detail.scss']
})
export class CallLogDetail extends DialogComponentBase implements OnInit {
  dialogTitle = _tk('call.detail');
  dialogRightActions: DialogActionButton[] = [];

  call: ICallLog;
  callList: any[] = [];
  callDuration: string;
  callNumber: string;

  activeCRM: EnterpriseCrmEntity;
  crmConfig: any;
  // voicemailMessages: {
  //   public_id: string;
  //   receive_dt: string;
  //   is_read: number;
  //   readers: number[];
  // }[];
  voicemailMessages: MissedCallVoicemail[];
  callDirection = CallDirection;
  isSMSFeatureEnabled = false;

  private readonly _crmContactUrls: any = {};
  private userId: number;

  constructor(
    dialogRef: MatDialogRef<CallLogDetail>,
    private readonly crmContactService: CrmContactService,
    private readonly cdrService: CdrService,
    private readonly authService: AuthenticationService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly calllogService: CallLogService,
    @Inject(MAT_DIALOG_DATA) public data: {
      call: ICallLog,
      onMakeCall: (phoneNumber: string, call: any) => void
    },
    private readonly em: EntityManager,
    private readonly dialogService: DialogService,
    cdr: ChangeDetectorRef,
    private logger: LoggerService
  ) {
    super(dialogRef, cdr);

    this.userId = this.authService.getUserId();
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.isSMSFeatureEnabled = ConfigManager.getInstance().hasFeature(FeatureName.SMS);

    this.call = this.data.call;
    this.callNumber = this.calllogService.getRemoteNumber(this.call);

    try {
      // Get call history: call log detail was used by both of missed-call and call-log list, so there're 2 webservices to get call-history
      if (this.call.missed_call_id) {
        const missedCallInfo: MissedCallInfo = await this.cdrService.get_missed_call_info(this.call.missed_call_id);
        this.callList = missedCallInfo.callHistory && missedCallInfo.callHistory.map(x => this.calllogService.cdrToCallLog(x));
        this.voicemailMessages = missedCallInfo.voicemailMessages;
      } else {
        const callHistory: CallHistoryItem[] = await this.cdrService.get_history_call_info(this.callNumber);
        const callList = callHistory && callHistory.length ? callHistory.filter(x => x.id !== this.call.id) : [];
        this.callList = callList.map(x => this.calllogService.cdrToCallLog(x));
        // console.log('get_history_call_info', this.callList);
      }

      this.cdRef.detectChanges();

      if (!this.activeCRM) {
        this.activeCRM = await this.crmContactService.getActiveCRM(false);
        this.crmConfig = this.crmContactService.getConfigCRM(this.activeCRM);
        if (this.activeCRM) {
          this._crmContactUrls[this.activeCRM.type] = {};
        }
        this.cdr.detectChanges();
      }
    } catch (error) {
      this.logger.error('Cannot get history missed call info', error);
    }
  }

  shouldDisplayRecall(): boolean {
    if (!this.call) {
      return false;
    }

    if (this.call.recall_request_status !== 1) {
      return false;
    }
    if (this.call.recall_time) {
      const now = localNow();
      const recallTime = parseDateTime(this.call.recall_time);
      // tslint:disable-next-line: strict-comparisons
      return recallTime && now >= recallTime;
    }

    return true;
  }

  makeCall(): void {
    if (this.data.onMakeCall) {
      this.data.onMakeCall(this.callNumber, this.call);
    } else {
      this.logger.error('No onMakeCall params given on data when calling dialog');
    }
  }

  async voicemailUpdateListened(item: MissedCallVoicemail): Promise<void> {
    if (item.is_read && _.includes(item.readers || [], this.userId)) {
      this.logger.error('Voicemail already listened');
      return;
    }
    await this.em.getRepository<VoiceMailRepository>('VoiceMailRepository').markVoiceMailListened(item.public_id);
    item.is_read = 1;
    if (!item.readers) {
      item.readers = [];
    }
    item.readers.push(this.userId);
    this.updateCallLogListenedVoicemailPublicId(item.public_id);
  }

  async updateListened(item: ICallLog): Promise<void> {
    if (item.is_read && _.includes(item.readers || [], this.userId)) {
      this.logger.error('Call already listened');
      return;
    }
    await this.em.getRepository<VoiceMailRepository>('VoiceMailRepository').markVoiceMailListened(item.voicemail_public_id);
    item.is_read = 1;
    if (!item.readers) {
      item.readers = [];
    }
    item.readers.push(this.userId);
    this.updateCallLogListenedVoicemailPublicId(item.voicemail_public_id);
  }

  updateCallLogListenedVoicemailPublicId(voicemailPublicId: string): void {
    if (!voicemailPublicId) {
      return;
    }

    const vm = this.voicemailMessages.find(i => i.public_id === voicemailPublicId);
    if (vm) {
      vm.is_read = 1;
    }

    const call = this.callList.find(c => c.voicemail_public_id === voicemailPublicId);
    if (call) {
      call.is_read = 1;
    }
    this.cdr.detectChanges();
  }

  async openContactProfile(phoneNumber: string): Promise<void> {
    const url = await this.getCrmContactUrl(phoneNumber);
    if (url) {
      window.open(url, '_blank');
    }
  }

  async getCrmContactUrl(phoneNumber: string): Promise<string> {
    if (!this.activeCRM) {
      this.logger.error('No enterprise crm active');
      return undefined;
    }
    let crmContactUrl = this._crmContactUrls[this.activeCRM.type][phoneNumber];
    if (!crmContactUrl) {
      try {
        const contactData = await this.crmContactService.getCrmContactByNumber(this.activeCRM.id, phoneNumber);
        crmContactUrl = contactData.profileUrl;
        this._crmContactUrls[this.activeCRM.type][phoneNumber] = crmContactUrl;
      } catch (e) {
        this.showError(e && e.error && e.error.message || _ti('crm_contact.message.not_found'));
      }
    }
    return crmContactUrl;
  }

  sendSms(): void {
    if (!this.isSMSFeatureEnabled) {
      this.logger.warn('Do not supported the SMS feature');
      return;
    }
    const data: ISentSmsModalData = {
      destination: this.callNumber
    };
    this.dialogService.openDialog2(SentSmsModalComponent, { data });
  }
}
